import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { CommKind } from '@masamune/shared';

interface Props {
  onClose: () => void;
  projectId?: string;
  clientId?: string;
  prefill?: { kind?: string; subject?: string; body?: string };
}

const KIND_LABELS: Record<string, string> = {
  EMAIL: 'Email', CALL: 'Chiamata', MEETING: 'Meeting', NOTE: 'Nota interna',
};

export function NewCommModal({ onClose, projectId, clientId, prefill }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    kind: (prefill?.kind as string) ?? CommKind.NOTE,
    subject: prefill?.subject ?? '',
    body: prefill?.body ?? '',
    fromText: '',
    toText: '',
    durationMin: 0,
    tag: '',
  });

  const create = useMutation({
    mutationFn: () => {
      const url = projectId
        ? `/projects/${projectId}/communications`
        : `/clients/${clientId}/communications`;
      return api.post(url, {
        ...form,
        durationMin: form.durationMin || null,
        tag: form.tag || null,
        fromText: form.fromText || null,
        toText: form.toText || null,
        occurredAt: new Date().toISOString(),
      });
    },
    onSuccess: () => {
      if (projectId) qc.invalidateQueries({ queryKey: ['project', projectId] });
      if (clientId) qc.invalidateQueries({ queryKey: ['client', clientId] });
      toast.ok('Comunicazione registrata', form.subject);
      onClose();
    },
    onError: (err) => toast.alert('Errore salvataggio comunicazione', err instanceof ApiError ? err.message : 'Riprova'),
  });

  return (
    <Modal
      title="Nuova comunicazione"
      kicker={projectId ? 'Progetto · Comunicazioni' : 'Cliente · Comunicazioni'}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-primary" onClick={() => create.mutate()} disabled={!form.subject || (!projectId && !clientId) || create.isPending}>
            Salva <Icon name="send" size={12} />
          </button>
        </>
      }
    >
      <div className="form-section">
        <div className="form-section-hd">Tipo</div>
        <div className="seg-control">
          {Object.values(CommKind).map((k) => (
            <button key={k} aria-pressed={form.kind === k} onClick={() => setForm({ ...form, kind: k })}>
              {KIND_LABELS[k]}
            </button>
          ))}
        </div>
      </div>

      <div className="form-grid">
        <Field label="Oggetto" required span={12}>
          <input className="inp" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} autoFocus />
        </Field>
        {(form.kind === 'EMAIL' || form.kind === 'CALL') && (
          <>
            <Field label="Da" span={6}>
              <input className="inp" placeholder="mittente@…" value={form.fromText} onChange={(e) => setForm({ ...form, fromText: e.target.value })} />
            </Field>
            <Field label="A" span={6}>
              <input className="inp" placeholder="destinatario@…" value={form.toText} onChange={(e) => setForm({ ...form, toText: e.target.value })} />
            </Field>
          </>
        )}
        {(form.kind === 'CALL' || form.kind === 'MEETING') && (
          <Field label="Durata (min)" span={6}>
            <input className="inp mono text-right" type="number" value={form.durationMin} onChange={(e) => setForm({ ...form, durationMin: Number(e.target.value) })} />
          </Field>
        )}
        <Field label="Tag" span={6}>
          <input className="inp" placeholder="sync, escalation, design..." value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} />
        </Field>
        <Field label="Corpo / Note" span={12}>
          <textarea className="inp" rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
        </Field>
      </div>
    </Modal>
  );
}
